import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Download, Share2, MessageSquare, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Organizer-side feedback panel: list dancers + counts, download per-dancer PDF, share via WhatsApp.
 *
 * Props:
 * - api: API base URL
 * - token: organizer auth token
 * - competitionId: id
 * - festivalName: optional, used in WhatsApp message
 * - category: optional, used in WhatsApp message
 */
export default function FeedbackManager({ api, token, competitionId, festivalName, category }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [withFeedbackOnly, setWithFeedbackOnly] = useState("with");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(
        `${api}/feedback/dancers-summary/${competitionId}`,
        { headers }
      );
      setItems(r.data?.dancers || []);
    } catch (e) {
      toast.error("Failed to load feedback summary");
    } finally {
      setLoading(false);
    }
  }, [api, token, competitionId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items
      .filter((d) => roleFilter === "all" || d.role === roleFilter)
      .filter((d) => withFeedbackOnly === "all" || d.feedback_count > 0)
      .filter((d) =>
        !s ||
        (d.name || "").toLowerCase().includes(s) ||
        String(d.bib_number || "").includes(s) ||
        (d.country || "").toLowerCase().includes(s)
      );
  }, [items, roleFilter, withFeedbackOnly, search]);

  const totalWith = items.filter((d) => d.feedback_count > 0).length;

  const downloadPdf = (dancer) => {
    window.open(`${api}/feedback/dancer/${dancer.dancer_id}/pdf`, "_blank");
  };

  const shareWhatsApp = (dancer) => {
    const pdfUrl = `${api}/feedback/dancer/${dancer.dancer_id}/pdf`;
    const head = festivalName ? `${festivalName}${category ? " · " + category : ""}` : "JackAndJillScore";
    const text =
      `Hello ${dancer.name}! 🎉\n\n` +
      `Here is the judge feedback from ${head}:\n${pdfUrl}\n\n` +
      `Thank you for competing!`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-4" data-testid="feedback-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-['Playfair_Display'] text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#FFD700]" />
            Judge Feedback
          </h2>
          <p className="text-[#A1A1AA] text-xs">
            {totalWith}/{items.length} dancers have feedback so far
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="btn-outline"
            onClick={() => window.open(`${api}/feedback/competition/${competitionId}/pdf`, "_blank")}
            data-testid="feedback-bulk-pdf-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Download all (PDF)
          </Button>
          <Button variant="outline" size="sm" className="btn-outline" onClick={refresh} data-testid="feedback-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="glass rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
          <Input
            placeholder="Search name / bib / country"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-black/30 border-white/10 text-white"
            data-testid="org-feedback-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="bg-black/30 border-white/10 text-white" data-testid="org-feedback-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="Leader">Leaders</SelectItem>
            <SelectItem value="Follower">Followers</SelectItem>
          </SelectContent>
        </Select>
        <Select value={withFeedbackOnly} onValueChange={setWithFeedbackOnly}>
          <SelectTrigger className="bg-black/30 border-white/10 text-white" data-testid="org-feedback-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="with">With feedback only</SelectItem>
            <SelectItem value="all">All dancers</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-center text-[#A1A1AA] py-8">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-[#52525B] py-8">No matching dancers.</p>
      ) : (
        <div className="space-y-2" data-testid="org-feedback-list">
          {filtered.map((d) => (
            <div
              key={d.dancer_id}
              className="glass rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`org-feedback-row-${d.dancer_id}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-sm"
                  style={{
                    background: d.role === "Leader" ? "#FFD70022" : "#FF007F22",
                    color: d.role === "Leader" ? "#FFD700" : "#FF007F",
                  }}
                >
                  #{d.bib_number || "?"}
                </div>
                <div>
                  <p className="text-white font-semibold leading-tight">{d.name}</p>
                  <p className="text-[#52525B] text-xs">
                    {d.role}
                    {d.country ? ` · ${d.country}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">
                  {d.feedback_count} {d.feedback_count === 1 ? "note" : "notes"}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="btn-outline"
                  onClick={() => downloadPdf(d)}
                  disabled={d.feedback_count === 0}
                  data-testid={`org-feedback-pdf-${d.dancer_id}`}
                >
                  <Download className="w-3 h-3 mr-1" />
                  PDF
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => shareWhatsApp(d)}
                  disabled={d.feedback_count === 0}
                  data-testid={`org-feedback-whatsapp-${d.dancer_id}`}
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  WhatsApp
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
